import React, { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { AppShell } from "@/components/AppShell";
import { Loading } from "@/components/common";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";

import Landing from "@/pages/Landing";
import AuthPage from "@/pages/AuthPage";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import Catalog from "@/pages/Catalog";
import CourseDetail from "@/pages/CourseDetail";
import LessonPage from "@/pages/LessonPage";
import QuizPage from "@/pages/QuizPage";
import TutorPage from "@/pages/TutorPage";
import CreatePage from "@/pages/CreatePage";
import ClassDetail from "@/pages/ClassDetail";
import ChildDetail from "@/pages/ChildDetail";
import Profile from "@/pages/Profile";
import BillingPage from "@/pages/BillingPage";
import AdminDashboard from "@/pages/AdminDashboard";
import InviteAccept from "@/pages/InviteAccept";
import Records from "@/pages/Records";
import Syllabus, { SyllabiIndex } from "@/pages/Syllabus";
import Policies from "@/pages/Policies";
import GedHub from "@/pages/GedHub";
import GedAttempt from "@/pages/GedAttempt";
import RealGedHandoff from "@/pages/RealGedHandoff";
import { PaymentSuccess, PaymentCancel } from "@/pages/PaymentResult";
import { Paywall } from "@/components/Billing";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn } = useAuth();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const m = location.hash.match(/session_id=([^&]+)/);
    const sid = m ? decodeURIComponent(m[1]) : null;
    if (!sid) {
      navigate("/login", { replace: true });
      return;
    }
    api
      .post("/auth/google/session", { session_id: sid })
      .then(({ data }) => {
        signIn(data.token, data.user);
        window.history.replaceState(null, "", window.location.pathname);
        navigate(data.user.role ? "/dashboard" : "/onboarding", { replace: true });
      })
      .catch((e) => {
        toast.error(errMsg(e, "Google sign-in failed"));
        navigate("/login", { replace: true });
      });
  }, [location.hash, navigate, signIn]);
  return <Loading label="Signing you in..." />;
};

const Protected = ({ children, roles, shell = true, paid = false }) => {
  const { user, loading, billing } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.role) return <Navigate to="/onboarding" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  if (paid && !billing) return <AppShell><Loading /></AppShell>;
  if (paid && !billing.access?.has_access) return <AppShell noBanner><Paywall /></AppShell>;
  return shell ? <AppShell>{children}</AppShell> : children;
};

const AppRouter = () => {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<AuthPage mode="login" />} />
      <Route path="/signup" element={<AuthPage mode="signup" />} />
      <Route path="/onboarding" element={<Onboarding />} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/catalog" element={<Protected><Catalog /></Protected>} />
      <Route path="/courses/:id" element={<Protected><CourseDetail /></Protected>} />
      <Route path="/lessons/:id" element={<Protected paid><LessonPage /></Protected>} />
      <Route path="/lessons/:id/quiz" element={<Protected paid><QuizPage /></Protected>} />
      <Route path="/tutor" element={<Protected paid><TutorPage /></Protected>} />
      <Route path="/tutor/:sid" element={<Protected paid><TutorPage /></Protected>} />
      <Route path="/create" element={<Protected paid><CreatePage /></Protected>} />
      <Route path="/classes/:id" element={<Protected roles={["teacher", "admin", "parent", "school"]}><ClassDetail /></Protected>} />
      <Route path="/children/:id" element={<Protected roles={["parent"]}><ChildDetail /></Protected>} />
      <Route path="/billing" element={<Protected><BillingPage /></Protected>} />
      <Route path="/payment/success" element={<Protected><PaymentSuccess /></Protected>} />
      <Route path="/payment/cancel" element={<Protected><PaymentCancel /></Protected>} />
      <Route path="/profile" element={<Protected><Profile /></Protected>} />
      <Route path="/invite/:token" element={<InviteAccept />} />
      <Route path="/records/:studentId" element={<Protected><Records /></Protected>} />
      <Route path="/syllabus/:id" element={<Syllabus />} />
      <Route path="/syllabi" element={<SyllabiIndex />} />
      <Route path="/policies" element={<Policies />} />
      <Route path="/ged" element={<Protected><GedHub /></Protected>} />
      <Route path="/ged/take-real-ged" element={<Protected><RealGedHandoff /></Protected>} />
      <Route path="/ged/attempts/:attemptId" element={<Protected paid><GedAttempt /></Protected>} />
      <Route path="/admin" element={<Protected roles={["admin"]}><AdminDashboard /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
